import type { IBotNetworkInterface, TMessage } from '../types/bot_interface';
import type {
  IBotNetwork,
  ICTMOptions,
  IFeedback,
  IMessage,
  t_message_direction,
  t_network_types
} from '../types/network';

import { MessagingFacade } from '../facades/messaging_facade';
import { FeedbackFacade } from '../facades/feedback_facade';
import { PointCutFacade } from '../facades/pointcut_facade';

import { SecurityModel } from '../models/security_model';
import { MessagesModel } from '../models/messages_model';

import { NetworkServant } from '../servants/network_servant';

import { NetworkDemo } from '../adapters/network_demo';
import { NetworkHTTP } from '../adapters/network_http';
import { NetworkNone } from '../adapters/network_none';
import { NetworkWS } from '../adapters/network_ws';

import f1 from '@eab-pip/pi-f1';

/**
 * a mediator between the messaging service and networking service so a new component
 * can be built easily without needing to be involved in the complexity of bot communication
 */
export class KnowledgeBot {
  /**
   * network service for sending/receiving messages from an endpoint
   */
  network: IBotNetwork = NetworkServant.defaultNetwork();

  /**
   * list of messages
   *
   * @note this must never be assigned to
   */
  private readonly __messages: IMessage[] = [];

  /**
   * pointcut service for adding before and after hooks
   */
  readonly pointCut = new PointCutFacade();

  /**
   * message list service for handling messages data structure
   */
  readonly messages = new MessagesModel(this.__messages);

  /**
   * security tools
   */
  readonly security = new SecurityModel(this);

  /**
   * messaging service for structuring and constructing messages
   */
  readonly messaging = new MessagingFacade(this.__messages, this);

  /**
   * feedback service for sending user feedback about messages
   */
  readonly feedback = new FeedbackFacade(this.messaging, this.__messages, this);

  /**
   * functions to augment behavior at runtime
   */
  substitutions: Record<string, Function[]> = {};

  /**
   * enables custom network adapters for the bot
   */
  customAdapters = [];

  /**
   * network adapters supported by the bot
   */
  adapters = [NetworkWS, NetworkHTTP, NetworkNone, NetworkDemo];

  /**
   * begin the network connection
   */
  async startup() {
    return this.pointCut.invokeAsync(this, 'startup', async () => {
      await this.messages.startup();
      await this.security.startup();
      return this.network.startup();
    });
  }

  /**
   * end the network connection
   */
  async shutdown() {
    return this.pointCut.invokeAsync(this, 'shutdown', async () => {
      await this.messages.shutdown();
      await this.security.shutdown();
      return this.network.shutdown();
    });
  }

  /**
   * Create the network connection with a, possibly, new protocol (ws, http, none)
   */
  async createNetwork(botInterface: IBotNetworkInterface) {
    return this.pointCut.invokeAsync({}, 'create-network', async () => {
      await this.network.shutdown();

      this.network = NetworkServant.createNetwork(this, botInterface, this.messaging) as unknown as IBotNetwork;
      return this.network;
    });
  }

  /**
   * identifies the network protocol type ws or http
   * @deprecated
   */
  identifyNetwork(): t_network_types {
    return this.network.ofType('ws')
      ? 'ws'
      : this.network.ofType('http')
      ? 'http'
      : this.network.ofType('demo')
      ? 'demo'
      : 'none';
  }

  /**
   * @returns true if there is already a message streaming
   */
  isMsgStreaming() {
    return this.network.inStreaming;
  }

  /**
   * listen for internal events when messaging.
   *
   * - `message-amended` when a message exists, but it is updated
   * - `message-added` when a new messaged is added
   * - `message-done` when the message streaming is finished
   * - `message-failed` when some system has a failure
   */
  on(type: 'message-amended', fn: (message: IMessage) => IMessage): void;
  on(type: 'message-added', fn: (message: IMessage) => IMessage): void;
  on(type: 'message-done', fn: (message: IMessage) => IMessage): void;
  on(type: 'message-failed', fn: (text?: string) => void): void;
  on(
    type: 'message-amended' | 'message-added' | 'message-done' | 'message-failed',
    fn: ((_: IMessage) => IMessage) | ((_?: string) => void)
  ): void {
    if ('message-amended' === type) {
      this.messaging.afterMessageAmended = (message: IMessage): IMessage =>
        this.pointCut.invokeSync(message, 'message-amended', fn);
    } else if ('message-added' === type) {
      this.messaging.afterMessageAdded = (message: IMessage): IMessage =>
        this.pointCut.invokeSync(message, 'message-added', fn);
    } else if ('message-done' === type) {
      this.messaging.afterMessageDone = (message: IMessage): IMessage =>
        this.pointCut.invokeSync(message, 'message-done', fn);
    } else if ('message-failed' === type) {
      this.messaging.afterMessageFailed = (text: string) => this.pointCut.invokeSync(text, 'message-failed', fn);
    }
  }

  /**
   * reset the messages and network
   */
  reset() {
    return this.pointCut.invokeAsync(this, 'reset', async () => {
      await this.shutdown();
      return this.startup();
    });
  }

  /**
   * push a new message to the bot but do not show the human the message
   */
  shareWith(text: object) {
    return this.pointCut.invokeAsync(text, 'share-with', (input: string) =>
      this.network.send(this.messaging.sendMessageToBot(input, 'private'))
    );
  }

  /**
   * push a new message to the bot
   */
  pushTo(text: string) {
    return this.pointCut.invokeAsync(text, 'push-to', (input: string) =>
      this.network.send(this.messaging.sendMessageToBot(input, 'public'))
    );
  }

  /**
   * write a message which came from the bot
   *
   * @note usually called by a networking module
   */
  pullFrom(text: string) {
    return this.pointCut.invokeAsync(text, 'pull-from', (input: string) => this.messaging.writeMessageFromBot(input));
  }

  /**
   * clear local messages
   */
  clearMessages() {
    return this.pointCut.invokeSync(this.messages, 'clear-messages', (messages: MessagesModel) => {
      messages.eraseAll();
    });
  }

  /**
   * insert a list of messages
   */
  insertMessages(messages: TMessage[]) {
    return this.pointCut.invokeSync(messages, 'insert-messages', (messages: TMessage[]) => {
      messages.forEach((message) => {
        this.insertMessage(message.html, message.direction, message);
      });
    });
  }

  /**
   * insert any message on to the messages list in either direction
   *
   * - `from-bot` a message from the bot to the human
   * - `to-bot` a message from the human to the bot
   */
  insertMessage(text: string, type: t_message_direction, message?: Partial<TMessage>) {
    return this.pointCut.invokeAsync(text, 'insert-message', (input: string) =>
      this.messaging.writeMessage(input, type, message)
    );
  }

  /**
   * send user feedback to the network
   */
  sendFeedback(feedback: IFeedback) {
    return this.pointCut.invokeAsync(feedback, 'send-feedback', (input: IFeedback) => this.network.sendFeedback(input));
  }

  /**
   * get legal notice to show as a first message to user
   */
  pullLegalNotice(options: { uri?: string; ctm?: ICTMOptions }) {
    if (options.ctm && !f1.is.empty(options.ctm) && options.ctm.endpoint_uri) {
      return NetworkServant.getLegalNoticeCTM(options.ctm);
    } else if (options.uri) {
      return NetworkServant.getLegalNoticeUri(options.uri);
    } else {
      return Promise.resolve();
    }
  }
}
